export const DISTINCT_PAGE_SIZE = 10;

export interface SelectPageState {
    offset: number;
    keyword: string;
    hasMore: boolean;
    loadingMore: boolean;
    totalCount: number | null;
}

export interface DistinctSelectOption {
    id: string;
    value: string;
}

export type DistinctItemsApi = 'tracker' | 'filter';

export interface DistinctItemsContext {
    table: string;
    dataSource: string | number;
    isExternal: boolean;
}

export interface DistinctItemsColumnStore {
    pages: Record<string, SelectPageState>;
    hasMore: Record<string, boolean>;
    loadingMore: Record<string, boolean>;
    items: Record<string, DistinctSelectOption[]>;
}

export function createDistinctItemsColumnStore(): DistinctItemsColumnStore {
    return {
        pages: {},
        hasMore: {},
        loadingMore: {},
        items: {},
    };
}

export function createSelectPageState(): SelectPageState {
    return {
        offset: 0,
        keyword: '',
        hasMore: true,
        loadingMore: false,
        totalCount: null,
    };
}

export function appendUniqueByKey<T>(
    current: T[],
    batch: T[],
    keyField: string
): T[] {
    const seen = new Set(
        current.map(item => String((item as Record<string, unknown>)[keyField]))
    );
    const merged = [...current];
    for (const item of batch) {
        const key = String((item as Record<string, unknown>)[keyField]);
        if (!seen.has(key)) {
            seen.add(key);
            merged.push(item);
        }
    }
    return merged;
}

export function updateHasMore(
    page: SelectPageState,
    batchLength: number,
    loadedLength: number,
    pageSize: number = DISTINCT_PAGE_SIZE,
    previousLoadedLength?: number
): void {
    if (batchLength === 0) {
        page.hasMore = false;
        return;
    }
    if (
        previousLoadedLength != null &&
        loadedLength <= previousLoadedLength
    ) {
        page.hasMore = false;
        return;
    }
    if (page.totalCount != null) {
        page.hasMore = loadedLength < page.totalCount;
        return;
    }
    page.hasMore = batchLength >= pageSize;
}

export function findDistinctItemEntry(
    entries: Array<Record<string, unknown>>,
    key: string
): Record<string, unknown> | undefined {
    return entries.find((entry) => {
        const columnName = String(entry['column_name'] ?? '');
        const fieldName = String(entry['fieldName'] ?? '');
        return columnName === key || fieldName === key;
    });
}

export function mapDistinctValues(values: string[]): DistinctSelectOption[] {
    return values.map(value => ({ id: value, value }));
}

/**
 * Ensures already-selected values appear in a server-paged distinct list even when they
 * were not on the currently loaded page (so mat-select can render them in the trigger).
 * Missing selections are prepended; existing rows are left in fetch order.
 */
export function seedSelectedDistinctOptions(
    items: DistinctSelectOption[],
    selectedValues: string[] | null | undefined
): DistinctSelectOption[] {
    if (!selectedValues?.length) {
        return items;
    }
    const existing = new Set(items.map(item => item.value.trim()));
    const missing: DistinctSelectOption[] = [];
    for (const raw of selectedValues) {
        const value = String(raw ?? '').trim();
        if (!value || existing.has(value)) {
            continue;
        }
        existing.add(value);
        missing.push({ id: value, value });
    }
    return missing.length > 0 ? [...missing, ...items] : items;
}

export function makeDistinctStoreKey(
    ...parts: (string | number)[]
): string {
    return parts.map(part => String(part)).join('::');
}

export function initDistinctColumn(
    store: DistinctItemsColumnStore,
    storeKey: string
): void {
    store.pages[storeKey] = createSelectPageState();
    store.hasMore[storeKey] = true;
    store.loadingMore[storeKey] = false;
    store.items[storeKey] = [];
}

export function removeDistinctColumn(
    store: DistinctItemsColumnStore,
    storeKey: string
): void {
    delete store.pages[storeKey];
    delete store.hasMore[storeKey];
    delete store.loadingMore[storeKey];
    delete store.items[storeKey];
}

export function pruneDistinctColumns(
    store: DistinctItemsColumnStore,
    keepKeys: Set<string>
): void {
    Object.keys(store.items).forEach((key) => {
        if (!keepKeys.has(key)) {
            removeDistinctColumn(store, key);
        }
    });
}

/** Returns false when the fetch should be skipped (already loading or no more pages). */
export function beginDistinctFetch(
    page: SelectPageState,
    reset: boolean,
    store: DistinctItemsColumnStore,
    storeKey: string
): boolean {
    if (!reset && (!page.hasMore || page.loadingMore)) {
        return false;
    }
    if (reset) {
        page.hasMore = true;
        page.totalCount = null;
    }
    page.loadingMore = !reset;
    store.loadingMore[storeKey] = page.loadingMore;
    return true;
}

export function applyDistinctFetchResult(
    page: SelectPageState,
    store: DistinctItemsColumnStore,
    storeKey: string,
    result: {
        mergedItems: DistinctSelectOption[];
        nextOffset: number;
        hasMore: boolean;
        totalCount: number | null;
    }
): void {
    store.items[storeKey] = result.mergedItems;
    page.offset = result.nextOffset;
    page.totalCount = result.totalCount;
    page.hasMore = result.hasMore;
    page.loadingMore = false;
    store.hasMore[storeKey] = result.hasMore;
    store.loadingMore[storeKey] = false;
}

export function failDistinctFetch(
    page: SelectPageState,
    store: DistinctItemsColumnStore,
    storeKey: string
): void {
    page.loadingMore = false;
    store.loadingMore[storeKey] = false;
}
